import { buildEverflowDomains } from '@pickleballinc/lib/everflow/url';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { APR_LABELS } from '@pickleballinc/lib/product-labels';
import { LEGAL_PAGES } from '@pickleballinc/ui/lib/legal-paths';

import { publicEnv } from '@/lib/env';

export const TICKER_PREFETCH_SIZE = '5';
export const TICKER_SCROLL_SIZE = '100';
export const TICKER_MATCH_CARD_WIDTH = 324;
export const HOMEPAGE_COLUMN_NUMBER_OF_WIDGETS = 10;
export const MATCH_CARDS_BUCKET_PATH = 'matchcards';

export const SITEMAP_PAGINATION_PAGE_SIZE = 45000;

export const USER_IMPRESSION_KEY = 'user-impression-key';
export const BLOG_LABEL = 'Pickleball Hub';
export const GOOGLE_AUTOCOMPLETE_SCRIPT_ID = 'google-script';

export const IS_PRODUCTION = publicEnv.NEXT_PUBLIC_APP_ENV === 'prod';
export const IS_DEVELOPMENT = publicEnv.NEXT_PUBLIC_APP_ENV === 'dev';
export const IS_TEST = publicEnv.NEXT_PUBLIC_APP_ENV === 'test';
export const IS_LOCALHOST = publicEnv.NEXT_PUBLIC_HOME_ROOT_URL?.startsWith('localhost') || false;
export const RTE_ENVIRONMENT = IS_PRODUCTION
  ? 'production'
  : IS_DEVELOPMENT
    ? 'development'
    : 'uat';

// No TEST host exists; test uses dev so testers aren't deep-linked into production games.
export const GAME_MAKER_DEEP_LINK_HOST = IS_PRODUCTION ? 'https://pbgm.io/' : 'https://pbgmio.dev/';

export const PICKLEBALL_PLACEHOLDER_IMAGE = `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/PB-gray.svg`;
export const CLUBS_PLACEHOLDER_IMAGE = `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/PC-gray.svg`;
export const LEAGUES_PLACEHOLDER_IMAGE = `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/PL-gray.svg`;
export const TOURNAMENT_PLACEHOLDER_IMAGE = `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/PT-gray.svg`;
export const TEAM_LEAGUES_PLACEHOLDER_IMAGE = `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/PTL-gray.svg`;
export const APR_PLACEHOLDER_IMAGE = `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/APR-gray.svg`;

export const FEATURED_PLAYERS_SINGLES = 'Singles';

export const ALLOWED_ORIGINS = [
  'http://localhost',
  'https://pickleball.com',
  'https://www.pickleball.com',
  'https://sso.pickleball.com',
  'https://www.sso.pickleball.com',
  'https://manage.pickleball.com',
  'https://www.manage.pickleball.com',
  'https://checkout.pickleball', // Sufficient, since it can be pickleball.com, pickleballclubs.com, pickleballtournaments.com, pickleballteamleagues.com and more (in all cases pickleball is the first word of the domain)
  'https://www.checkout.pickleball',
  'https://pickleballleagues.com',
  'https://www.pickleballleagues.com',
  `https://${APR_LABELS.domainProd}`,
  `https://www.${APR_LABELS.domainProd}`,
  'https://pickleballtournaments.com',
  'https://www.pickleballtournaments.com',
  'https://pickleballclubs.com',
  'https://www.pickleballclubs.com',
  'https://pickleballteamleagues.com',
  'https://www.pickleballteamleagues.com',
  'https://pickleballdev.net',
  'https://www.pickleballdev.net',
  'https://sso.pickleballdev.net',
  'https://www.sso.pickleballdev.net',
  'https://manage.pickleballdev.net',
  'https://www.manage.pickleballdev.net',
  'https://pickleballleaguesdev.net',
  'https://www.pickleballleaguesdev.net',
  `https://${APR_LABELS.domainDev}`,
  `https://www.${APR_LABELS.domainDev}`,
  'https://pickleballtournamentsdev.net',
  'https://www.pickleballtournamentsdev.net',
  'https://pickleballclubsdev.net',
  'https://www.pickleballclubsdev.net',
  'https://pickleballteamleaguesdev.net',
  'https://www.pickleballteamleaguesdev.net',
];

export const PBRACKETS_UPLOADS_ROOT = `${publicEnv.NEXT_PUBLIC_PBRACKETS_CDN_URL}/uploads`;

const MLP_ORG_UUID_PROD = 'f6f5296b-b9a6-4d25-a295-29f461de56d2';
const MLP_TL_UUID_PROD = '2f129d1d-0202-4d55-8448-bfafc0872305';
const MLP_TL_SLUG_PROD = 'major-league-pickleball';
const MLP_AU_TL_UUID_PROD = 'e5a624c8-4544-4479-9ec8-777301610133';
const MLP_ORG_UUID_DEV = 'c1e16af8-4b0e-456d-a8a8-d0fe1d5d630c';
const MLP_TL_UUID_DEV = '4e239924-3cba-4ce9-a996-26925ef940d1';
const MLP_TL_SLUG_DEV = 'test_league_mlp';
const MLP_AU_TL_UUID_DEV = '';

export const MLP_ORG_UUID = !IS_PRODUCTION ? MLP_ORG_UUID_DEV : MLP_ORG_UUID_PROD;
export const MLP_TL_UUID = !IS_PRODUCTION ? MLP_TL_UUID_DEV : MLP_TL_UUID_PROD;
export const MLP_AU_TL_UUID = !IS_PRODUCTION ? MLP_AU_TL_UUID_DEV : MLP_AU_TL_UUID_PROD;
export const MLP_TL_SLUG = !IS_PRODUCTION ? MLP_TL_SLUG_DEV : MLP_TL_SLUG_PROD;

// Left for future testing team league that has matchups with multiple pods
// const NPL_ORG_UUID_PROD = '88b1bbcd-7a65-4823-8e2c-5f5fc0c026e0';
// const NPL_TL_UUID_PROD = 'c420f83a-b066-4356-bbeb-894590dc0e84';
// const NPL_ORG_UUID_DEV = '4d060010-2541-45af-8100-7432c37c5875';
// const NPL_TL_UUID_DEV = '657a7b45-4b34-4c7b-9fb2-a986a2177da8';

// export const NPL_ORG_UUID = IS_PRODUCTION ? NPL_ORG_UUID_PROD : NPL_ORG_UUID_DEV;
// export const NPL_TL_UUID = IS_PRODUCTION ? NPL_TL_UUID_PROD : NPL_TL_UUID_DEV;

export const GTM_ID_PBCOM = 'GTM-NSBB9F4L';
export const MS_CLARITY_ID_PBCOM = 'rcs6udt4io';

export const WHITE_LISTED_PAGES = ['/players', '/results', '/leagues'];

/**
 * Pages that must render no Freestar scripts, slots or products at all.
 *
 * Matched with `isAdFreePath` from `@pickleballinc/ui/lib/freestar-ad-free-paths`:
 * `*` is exactly one segment, `**` is the rest of the path.
 */
export const AD_FREE_PAGES = [
  // My dashboard (players v3) and every sub page. `/players/<slug>` on its own is the public
  // profile and keeps its ads, which is why the slug is matched as a single segment.
  '/players/*/v3/**',
  // Edit profile (players v2) and every sub page.
  '/players/*/profile/**',
  '/head-to-head',
  '/results/match/*',
  '/results/matchup/*',
  // Only the routes *beneath* the league session page, not the session page itself. Game day is
  // `/matchdate/<id>`; `/club/<id>` is the club view under the same session. The session page keeps
  // its leaderboard, which means the League Schedule tab keeps it too — Schedule is `?tab=schedule`
  // on that same pathname, so it cannot be separated from Standings/Info/Sponsors/RSVP by path.
  '/leagues/*/session/*/matchdate/**',
  '/leagues/*/session/*/club/**',
  '/leagues/my-schedule/**',
  // Legal pages. They declare no placements of their own, so this is purely about keeping pubfig's
  // site-wide products (the sticky footer) off them. `/privacy-terms-dmca` is the chrome-less variant
  // and the `(with-nav)` pages are also served into an iframe via `?iframe=true`, which is the last
  // place an anchor ad belongs. Shared with FloatingMenu, which hides itself on these same paths in
  // a webview.
  ...LEGAL_PAGES,
];

export const MLP_TEMP_SEASON_UUID = 'c90dcfd5-6209-49db-842d-48a6e1827bf9';
export const MLP_TEMP_UTC_OFFSET = -18000;

export const THEME_LOOKUP = {
  purple: {
    backgroundColor: 'bg-purple-700',
    buttonBackgroundColor: '!bg-white',
    buttonTextColor: '!text-black',
    textColor: 'text-white',
    linkColor: 'text-white',
    emojiBackgroundColor: 'bg-white',
  },
  white: {
    backgroundColor: 'bg-white',
    buttonBackgroundColor: '!bg-purple-700',
    buttonTextColor: '!text-white',
    textColor: 'text-black',
    linkColor: 'text-purple-700',
    emojiBackgroundColor: 'bg-gray-200',
  },
};

export const BETTING_SITES_OPTIONS = [
  { label: 'Kalshi', value: 'BETTING_SITE_KALSHI' },
  { label: 'Polymarket', value: 'BETTING_SITE_POLYMARKET' },
  { label: 'DraftKings', value: 'BETTING_SITE_DRAFTKINGS' },
  { label: 'FanDuel', value: 'BETTING_SITE_FANDUEL' },
  { label: 'BetMGM', value: 'BETTING_SITE_BETMGM' },
  { label: 'ESPN Bet', value: 'BETTING_SITE_ESPN_BET' },
  { label: 'Other', value: 'BETTING_SITE_OTHER' },
];

export const PICKLEBALL_AXEPTIO_CLIENT_ID = '66e1485259bb6037f2b40b9b';
export const LEAGUES_AXEPTIO_CLIENT_ID = '69393a8482fb8f3156c0185d';
export const APR_AXEPTIO_CLIENT_ID = '693933d582fb8f3156c017b8';

export const FEATURE_FLAGS = {
  ENABLE_RANKING_BADGES: IS_DEVELOPMENT,
  ENABLE_NEW_CARDS: IS_DEVELOPMENT,
  // Which checkout a league registers through is not a build-time flag: it is `CheckoutVersion`
  // off API_v2_League_Reg_GetRequirements, read via useLeagueCheckoutVersion.
  ENABLE_APR_COUNTDOWN: IS_DEVELOPMENT,
  ENABLE_NEW_LEAGUES_PRICING_PAGE: IS_DEVELOPMENT,
} as const;

export const NEWSLETTER_IMAGE_URL = getCDNURL(
  `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/newsletter/newsletter-widget.png`,
  296,
  288
);
export const NEWSLETTER_FOOTER_MOBILE_IMAGE_URL = getCDNURL(
  `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/newsletter/newsletter-footer-mobile.png`,
  375,
  288
);
export const NEWSLETTER_FOOTER_IMAGE_URL = getCDNURL(
  `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/newsletter/newsletter-footer-2.png`,
  1440,
  316
);

export const NEWSLETTER_FOOTER_COVER_IMAGE_URL = getCDNURL(
  `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/newsletter/newsletter-footer-cover.png`,
  1440,
  432
);

export const PRIVACY_POLICY_URL = `https://${publicEnv.NEXT_PUBLIC_HOME_ROOT_URL}/privacy-policy`;

/**
 * Registrable domains the Everflow link decorator treats as ours (apex URLs only — every subdomain such as
 * checkout.*, sso.*, brackets.* matches by suffix). See docs/everflow-tracking.md.
 */
export const EVERFLOW_DOMAINS = buildEverflowDomains([
  `https://${publicEnv.NEXT_PUBLIC_HOME_ROOT_URL}`,
  publicEnv.NEXT_PUBLIC_PT_URL,
  publicEnv.NEXT_PUBLIC_PL_URL,
  publicEnv.NEXT_PUBLIC_PC_URL,
  publicEnv.NEXT_PUBLIC_PTL_URL,
  publicEnv.NEXT_PUBLIC_APR_URL,
  publicEnv.NEXT_PUBLIC_PBRACKETS_URL,
]);
